import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Checkbox,
  Group,
  Loader,
  Modal,
  Stack,
  Text,
  TextInput,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconCheck } from '@tabler/icons-react';
import { companyService } from '@/api/services/CompanyService';
import { useProfile } from '@/hooks/useProfile';
import type { BankAccount, BankInfo } from '@/types/company';
import IdText from '@/components/common/IdText';

type FieldStatus = 'idle' | 'checking' | 'valid' | 'invalid' | 'duplicate';

interface AddBankDialogProps {
  readonly opened: boolean;
  readonly existing: BankAccount[];
  readonly onAdd: (account: BankAccount) => void;
  readonly onClose: () => void;
}

const DEBOUNCE = 300;

// Добавление банковского счёта: БИК → подтянуть банк/К-с → валидация Р/с.
export default function AddBankDialog({ opened, existing, onAdd, onClose }: AddBankDialogProps) {
  const { t } = useTranslation();
  const { data: profile } = useProfile();
  const [bik, setBik] = useState('');
  const [rs, setRs] = useState('');
  const [isMain, setIsMain] = useState(false);
  const [bankInfo, setBankInfo] = useState<BankInfo | null>(null);
  const [bikStatus, setBikStatus] = useState<FieldStatus>('idle');
  const [rsStatus, setRsStatus] = useState<FieldStatus>('idle');

  // Сброс при открытии. Первый счёт по умолчанию — основной.
  useEffect(() => {
    if (opened) {
      setBik('');
      setRs('');
      setIsMain(existing.length === 0);
      setBankInfo(null);
      setBikStatus('idle');
      setRsStatus('idle');
    }
  }, [opened, existing.length]);

  // БИК → проверка через bankInfo (debounce).
  useEffect(() => {
    if (!opened) {
      return;
    }
    if (!/^\d{9}$/.test(bik)) {
      setBankInfo(null);
      setBikStatus(bik ? 'invalid' : 'idle');
      return;
    }
    setBikStatus('checking');
    const handle = window.setTimeout(() => {
      companyService
        .getBankByBik(bik)
        .then((info) => {
          if (info) {
            setBankInfo(info);
            setBikStatus('valid');
          } else {
            setBankInfo(null);
            setBikStatus('invalid');
          }
        })
        .catch(() => {
          setBikStatus('idle');
          notifications.show({ color: 'red', message: t('auth.errors.generic') });
        });
    }, DEBOUNCE);
    return () => window.clearTimeout(handle);
  }, [bik, opened, t]);

  // Р/с → уникальность + validate/rs (debounce). Активно только при валидном БИК.
  useEffect(() => {
    if (!opened || bikStatus !== 'valid') {
      setRsStatus('idle');
      return;
    }
    if (!/^\d{20}$/.test(rs)) {
      setRsStatus(rs ? 'invalid' : 'idle');
      return;
    }
    if (existing.some((account) => account.rs === rs)) {
      setRsStatus('duplicate');
      return;
    }
    setRsStatus('checking');
    const handle = window.setTimeout(() => {
      companyService
        .validateRs(bik, rs, profile?.type ?? '')
        .then((ok) => setRsStatus(ok ? 'valid' : 'invalid'))
        .catch(() => {
          setRsStatus('idle');
          notifications.show({ color: 'red', message: t('auth.errors.generic') });
        });
    }, DEBOUNCE);
    return () => window.clearTimeout(handle);
  }, [rs, bik, bikStatus, opened, existing, profile?.type, t]);

  const bikError =
    bikStatus === 'invalid'
      ? /^\d{9}$/.test(bik)
        ? t('app.company.banking.errBikNotFound')
        : t('app.company.banking.errBikFormat')
      : undefined;
  const rsError =
    rsStatus === 'duplicate'
      ? t('app.company.banking.errRsDuplicate')
      : rsStatus === 'invalid'
        ? /^\d{20}$/.test(rs)
          ? t('app.company.banking.errRsInvalid')
          : t('app.company.banking.errRsFormat')
        : undefined;

  const canSave = bikStatus === 'valid' && rsStatus === 'valid' && bankInfo !== null;

  function handleSave(): void {
    if (!canSave || !bankInfo) {
      return;
    }
    onAdd({
      bik,
      rs,
      ks: bankInfo.ks,
      name: bankInfo.name,
      inn: bankInfo.inn,
      kpp: bankInfo.kpp,
      logoUrl: bankInfo.logoUrl,
      isMain,
    });
  }

  return (
    <Modal opened={opened} onClose={onClose} centered title={t('app.company.banking.addTitle')}>
      <Stack gap="md">
        <TextInput
          label={t('app.company.banking.bik')}
          inputMode="numeric"
          maxLength={9}
          data-autofocus
          value={bik}
          onChange={(event) => setBik(event.currentTarget.value.replace(/\D/g, '').slice(0, 9))}
          error={bikError}
          rightSection={bikStatus === 'checking' ? <Loader size="xs" /> : null}
        />

        {bankInfo ? (
          <Stack gap={2}>
            <Text fw={500} size="sm">
              {bankInfo.name}
            </Text>
            {bankInfo.ks ? (
              <Text c="gray.7" size="sm">
                {t('app.company.banking.ks')}: <IdText>{bankInfo.ks}</IdText>
              </Text>
            ) : null}
          </Stack>
        ) : null}

        <TextInput
          label={t('app.company.banking.rs')}
          inputMode="numeric"
          maxLength={20}
          disabled={bikStatus !== 'valid'}
          value={rs}
          onChange={(event) => setRs(event.currentTarget.value.replace(/\D/g, '').slice(0, 20))}
          error={rsError}
          rightSection={
            rsStatus === 'checking' ? (
              <Loader size="xs" />
            ) : rsStatus === 'valid' ? (
              <IconCheck size={18} color="var(--mantine-color-teal-6)" />
            ) : null
          }
        />

        <Checkbox
          label={t('app.company.banking.mainAccount')}
          checked={isMain}
          disabled={existing.length === 0}
          onChange={(event) => setIsMain(event.currentTarget.checked)}
        />

        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button onClick={handleSave} disabled={!canSave}>
            {t('app.profile.save')}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
